import { HdGateGraphLayout, HdGateGraphNode } from './hd-gate-graph.models';
import { trimEdge } from './hd-gate-graph-layout';

/** Kleurcirkel — los element, want hij draagt zijn cijfer 1..6. */
export interface HdGateGraphColourVm {
  readonly id: string;
  readonly num: number;
  readonly x: number;
  readonly y: number;
  readonly radius: number;
}

/** Eén gebatcht path: alle vormen van één laag binnen één tak. */
export interface HdGateGraphPathVm {
  readonly layer: number;
  readonly d: string;
}

export interface HdGateGraphBranchVm {
  readonly id: string;
  readonly line: HdGateGraphNode;
  /** Connectoren per laag (4 t/m 1), diepste eerst zodat ze onder de cirkels landen. */
  readonly edgePaths: readonly HdGateGraphPathVm[];
  readonly baseCirclesPath: string;
  readonly toneCirclesPath: string;
  readonly colours: readonly HdGateGraphColourVm[];
}

export interface HdGateGraphScene {
  readonly root: HdGateGraphNode;
  readonly branches: readonly HdGateGraphBranchVm[];
  readonly extent: number;
}

const round = (value: number): number => Math.round(value * 100) / 100;

/**
 * Cirkel als twee halve bogen, zodat een hele laag in één `<path>` past. Dit is
 * wat de tekening van ±2700 elementen naar ±200 brengt.
 */
export function circleSubpath(node: Pick<HdGateGraphNode, 'x' | 'y' | 'radius'>): string {
  const r = round(node.radius);
  return `M${round(node.x - node.radius)} ${round(node.y)}`
    + `a${r} ${r} 0 1 0 ${round(node.radius * 2)} 0`
    + `a${r} ${r} 0 1 0 ${round(-node.radius * 2)} 0`;
}

/** Connector van ouder naar kind, ingekort tot de cirkelranden. */
export function edgeSubpath(parent: HdGateGraphNode, child: HdGateGraphNode): string {
  const edge = trimEdge(parent, child);
  return `M${round(edge.x1)} ${round(edge.y1)}L${round(edge.x2)} ${round(edge.y2)}`;
}

/**
 * Zet de layout om in render-klare primitieven: per tak één path per laag voor
 * de connectoren, één path voor alle basissen, één voor alle tonen, en de zes
 * kleurcirkels los omdat die een cijfer dragen.
 *
 * Pure functie — testbaar zonder TestBed.
 */
export function buildHdGateGraphScene(layout: HdGateGraphLayout): HdGateGraphScene {
  const branches = layout.root.children.map<HdGateGraphBranchVm>(line => {
    const colours = line.children;
    const tones = colours.flatMap(colour => colour.children);
    const bases = tones.flatMap(tone => tone.children);
    const nodesByLayer: readonly (readonly HdGateGraphNode[])[] = [[line], colours, tones, bases];

    return {
      id: line.id,
      line,
      edgePaths: [4, 3, 2, 1].map(layer => ({
        layer,
        d: nodesByLayer[layer - 1].map(node => edgeSubpath(node.parent!, node)).join(''),
      })),
      baseCirclesPath: bases.map(circleSubpath).join(''),
      toneCirclesPath: tones.map(circleSubpath).join(''),
      colours: colours.map(colour => ({
        id: colour.id,
        num: colour.num!,
        x: round(colour.x),
        y: round(colour.y),
        radius: colour.radius,
      })),
    };
  });

  return { root: layout.root, branches, extent: layout.extent };
}
